<script lang="ts" setup>
// {{{ imports
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js'
import moment from 'moment'
import {
  AliyahType,
  BarMitzvahType,
  HaftarahType,
  ParashahType,
  SentenceGroupType,
  SentenceType,
  TagType,
  TorahColumnOverlayType,
} from 'types/types'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRefs, watch, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { onBeforeRouteLeave } from 'vue-router'

import FullTextOverview from '@/components/FullTextOverview.vue'
import SentenceFocus from '@/components/SentenceFocus.vue'
import StudentToSentencesSelect from '@/components/StudentToSentencesSelect.vue'
import { useFollower } from '@/composables/follower'
import { makeSentenceSegmentation, makeWordSegmentation } from '@/composables/segmentation'
import { useSentences } from '@/composables/sentences'
import { useAudioPlayback } from '@/composables/useAudioPlayback'
import { useAudioRecording } from '@/composables/useAudioRecording'
import { RecordingMelodies, useRecordingManagement } from '@/composables/useRecordingManagement'
import { useSentenceNavigation } from '@/composables/useSentenceNavigation'
import { useVoiceStyles } from '@/composables/useVoiceStyles'
import {
  availablePhraseLevels,
  DEFAULT_TROPE_LEVEL,
  nearestAvailableLevel,
  PHRASE_LEVEL_SENTENCE,
  PHRASE_LEVEL_WORDS,
  type PhraseLevelInfo,
  segmentationForLevel,
} from '@/composables/versePhraser'
import {
  DEFAULT_SILENCE_PATIENCE,
  FOCUS_DIALOG_SCRIM_OPACITY,
  FOCUS_DIALOG_WIDTH,
  focusTextSegmentId,
  MAX_WORDS_STILL_PHRASED,
  RECORDING_MODE_SINGLE,
  type RecordingMode,
  UPLOAD_FAILED_TITLE,
  VOICE_MODE_READING,
  VOICE_MODE_SINGING,
  VOICE_SENSITIVITY_DEFAULT,
} from '@/constants/practiceConstants'
import ExtendColumnRange from '@/mutations/ExtendColumnRange.graphql'
import UploadRecording from '@/mutations/UploadRecording.graphql'
import store from '@/store'
import { MUTATION_FAILED_MESSAGE, runReportedMutation } from '@/store/mutationHelpers'
import { rankName } from '@/util/Aliyah'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { mimeToExtension, type SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { RecordingType } from '@/util/Recording'
import { manualPhraseAction, shouldAutoAdvanceAfterUpload, voiceStopAction } from '@/util/recordingFlow'
import { isSung } from '@/util/RecordingHelpers'
import { filterVisibleTags, PHRASES, phrasingFromTags, SENTENCE, WORDS } from '@/util/Tags'
import { waitingStyle } from '@/util/TextStyles'
import { defaultTransformation } from '@/util/Transformations'
import { inSingMode, READ, RecordingVoiceStyles, SING, type VoiceStyleSet, voiceStyleSetFromRecording } from '@/util/VoiceStyles'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// }}}
// {{{ setup
const logger = useLogger()

// Basic state for text selection
const state = reactive({
  aliyah: null as AliyahType | null,
  barMitzvah: null as BarMitzvahType | null,
  // Set when a column is freshly uploaded+analyzed, so FullTextOverview drops the
  // teacher into edit mode once the column renders; FullTextOverview clears it.
  columnAutoEdit: false,
  columnOverlay: null as TorahColumnOverlayType | null,
  haftarah: null as HaftarahType | null,
  manualStop: false,
  parashah: null as ParashahType | null,
  recordingMode: RECORDING_MODE_SINGLE as RecordingMode,
  replacementRecordingSegmentIndex: null as number | null,
  sentenceGroup: null as SentenceGroupType | null,
  sentences: [] as Array<SentenceType>,
  voiceDetected: false,
})

// Convert to refs for composables
const { aliyah, barMitzvah, haftarah, sentenceGroup, sentences } = toRefs(state)

// StudentToSentencesSelect owns column sentence loading; we call its exposed setter
// to move the verse range (column extend) and have it reload + emit the sentences.
const stssRef = ref<{ setColumnOverlay: (overlay: TorahColumnOverlayType | null) => void } | null>(null)

// Initialize composables with proper arguments
const recordingComposable = useAudioRecording(logger)
const playbackComposable = useAudioPlayback(logger)
const managementComposable = useRecordingManagement(logger)
const navigationComposable = useSentenceNavigation(logger, sentences)
const voiceStylesComposable = useVoiceStyles(
  logger,
  barMitzvah,
  aliyah,
  haftarah,
  sentenceGroup,
  recordingComposable.voiceStyleSet,
  navigationComposable.existingSentenceRecordingMetadata,
  recordingComposable.isRecording,
  recordingComposable.autoSegmentDetect,
)
// Extract recording state
const {
  addManualSegmentSplit,
  backgroundSampling,
  backgroundSamplingPercent,
  chartData,
  chartOptions,
  disableAutomaticVoiceStartStop,
  displayGraph,
  isReadyForRecording,
  isRecording,
  pendingUploadRecording,
  showStartNow,
  toggleRecord,
  uploadWaiting,
  voiceStyleSet,
  warnManualSegmentation,
} = recordingComposable

// {{{ follower integration — segment highlighting during recording
const {
  currentSegmentIndex: followerSegmentIndex,
  currentStyle,
  followerOptions,
  followerSegmentStyle,
  setCurrentSegment,
} = useFollower(recordingComposable.recorderService)

// During recording, highlight the segment the singer should sing next (waitingStyle) BEFORE
// they start, mirroring the pre-refactor setCurrentSegmentColorWrapper. Once the VAD detects
// voice, onVoiceStart switches the current segment to userSpeakingStyle.
function highlightSegmentToSing(segmentIndex: number): void {
  setCurrentSegment(0, segmentIndex)
  currentStyle.value = waitingStyle
}

// Chain follower callbacks into the RecorderService that useAudioRecording already created.
// The RS options are read lazily (when startRecording() creates the VAD), so updating them here works.
const rs = recordingComposable.recorderService.value!
const origOnStartRecording = rs.options!.onStartRecording
const origOnStopRecording = rs.options!.onStopRecording
const origOnBaseLevel = rs.options!.onBaseLevel

rs.options!.onVoiceStart = () => {
  state.voiceDetected = true
  console.log('RecordingsView: onVoiceStart, followerSegmentIndex =', followerSegmentIndex.value)
  followerOptions.onVoiceStart?.(focusTextSegmentId(0, followerSegmentIndex.value))
}
rs.options!.onVoiceStop = () => {
  return new Promise<void>((resolve) => {
    // Reset follower visual state on every VAD stop so the speaking highlight doesn't get stuck.
    followerOptions.onVoiceStop?.()

    const action = voiceStopAction({
      currentSegment: followerSegmentIndex.value,
      isReplacement: state.replacementRecordingSegmentIndex !== null,
      manualMode: disableAutomaticVoiceStartStop.value,
      nSegments: nFocusSegments.value,
    })
    console.log('RecordingsView: onVoiceStop, segment =', followerSegmentIndex.value, 'action =', action)

    if (action === 'stop') {
      toggleRecord()
    } else if (action === 'advance') {
      highlightSegmentToSing(followerSegmentIndex.value + 1)
    }
    resolve()
  })
}
rs.options!.onRecordingReady = async (e: CustomEvent) => {
  const recording = e.detail.recording as RecordingType
  console.log('onRecordingReady: voiceDetected =', state.voiceDetected, 'manualStop =', state.manualStop, 'size =', recording.size, 'bytes')
  if (!state.voiceDetected) {
    console.log('onRecordingReady: discarding recording — no voice detected')
    closeRecordOverlay()
    return
  }
  uploadRecording(recording)
}
rs.options!.onStartRecording = () => {
  origOnStartRecording?.()
  state.voiceDetected = false
  rs.applyVadSettings(VOICE_SENSITIVITY_DEFAULT, DEFAULT_SILENCE_PATIENCE)
  const startSegment = state.replacementRecordingSegmentIndex ?? 0
  console.log('RecordingsView: onStartRecording, startSegment =', startSegment)
  highlightSegmentToSing(startSegment)
  followerOptions.onStartRecording?.()
}
rs.options!.onStopRecording = () => {
  console.log('RecordingsView: onStopRecording')
  origOnStopRecording?.()
  followerOptions.onStopRecording?.()
}
rs.options!.onBaseLevel = (bl: number, perc: number) => {
  origOnBaseLevel?.(bl, perc)
  followerOptions.onBaseLevel?.(bl, perc)
}
rs.options!.onTriggerLevel = followerOptions.onTriggerLevel
// }}}

// Extract playback state
const {
  downloadingSentenceRecordingId,
  fetchRecordingForDownload,
  pause,
  paused,
  playSegment,
  playSentenceOrSequencedSegments,
  playing,
  playingSentenceRecordingId,
  purge: purgeAudioPlayer,
  resume,
} = playbackComposable

// Extract management state
const { clearAllRefetchIntervals, confirmDeleteRecording, sentenceRecordingMetadatas, updateRecordingMelodies } = managementComposable

// Extract navigation state
const {
  annotatedSentence: focusAnnotatedSentence,
  canProceedToSentence,
  closeRecordOverlay,
  existingSentenceRecordingMetadata,
  focusSentence,
  focusSentenceIndex,
  handleEditRecording,
  handleSentenceClick,
  phraseLevel,
  proceedToSentence,
  segmentation,
  segmentations,
} = navigationComposable

// Extract voice styles state
const {
  autoSelectMelody,
  haftarahMelodyChoices,
  inReadMode,
  isMelodicStyleDisabled,
  nonHiddenUserTags,
  phrasingDropdownDisabled,
  pronunciationChoices,
  sentenceGroupMelodyChoices,
  torahMelodyChoices,
} = voiceStylesComposable

// Additional state for UI
const { watchSentences } = useSentences(sentences, logger)

// Computed properties
const annotatedSentences = computed(() => {
  logger.debug('Computing annotatedSentences', {
    firstSentence: state.sentences[0],
    segmentation,
    sentencesCount: state.sentences.length,
  })

  return state.sentences.map((sentence, index) => {
    try {
      const segmentationArray = Array.isArray(segmentation.value) ? segmentation.value : []
      const annotatedSentence = new AnnotatedSentence(sentence, null, segmentationArray)
      logger.debug('Created annotated sentence', { index, sentenceId: sentence.id, text: sentence.text })
      return annotatedSentence
    } catch (error) {
      logger.error('Error creating annotated sentence', { error, index, sentence })
      return new AnnotatedSentence(sentence, null, [])
    }
  })
})

const canProceedToPrevious = computed(() => {
  return focusSentenceIndex.value !== null && canProceedToSentence(focusSentenceIndex.value - 1)
})

const canProceedToNext = computed(() => {
  return focusSentenceIndex.value !== null && canProceedToSentence(focusSentenceIndex.value + 1)
})

const transformation = defaultTransformation()

const printTitle = computed(() => {
  const ltrParts: string[] = []
  const rtlParts: string[] = []
  const student = state.barMitzvah?.student
  const studentName = student ? `${student.firstName} ${student.lastName}` : ''
  if (state.aliyah) {
    const parashah = state.parashah
    if (parashah) {
      ltrParts.push(parashah.transliteratedTitle)
      rtlParts.push(parashah.heTitle)
    }
    rtlParts.push(rankName(state.aliyah.rank))
  } else if (state.haftarah) {
    const parashah = state.haftarah.parashah
    ltrParts.push(`Haftarah — ${parashah.transliteratedTitle}`)
    rtlParts.push(parashah.heTitle)
  } else if (state.columnOverlay) {
    const parashah = state.parashah
    if (parashah) {
      ltrParts.push(parashah.transliteratedTitle)
      rtlParts.push(parashah.heTitle)
    }
    ltrParts.push(state.columnOverlay.name)
  } else if (state.sentenceGroup) {
    ltrParts.push(state.sentenceGroup.reading.description ?? 'Text')
  }
  return { ltr: ltrParts.join(' — '), rtl: rtlParts.join(' — '), studentName }
})

const userIsLoggedIn = computed(() => {
  return store.getters.user.userIsLoggedIn
})

const nFocusSegments = computed(() => {
  return focusAnnotatedSentence.value?.nSegments() ?? 0
})

const segmentableSentence = computed(() => {
  return focusAnnotatedSentence.value ? focusAnnotatedSentence.value.nWords() > MAX_WORDS_STILL_PHRASED : false
})

// Convenience: compute segmentation for the focused sentence at a given level
const focusSegmentationForLevel = (level: number) =>
  focusAnnotatedSentence.value
    ? segmentationForLevel(focusAnnotatedSentence.value.sentence(), focusAnnotatedSentence.value.nWords(), level)
    : []

// Only levels that produce distinct segmentations for this sentence — drives the dynamic slider
const availableLevels = computed<PhraseLevelInfo[]>(() => {
  if (!focusAnnotatedSentence.value) {
    return []
  }
  return availablePhraseLevels(focusAnnotatedSentence.value.sentence(), focusAnnotatedSentence.value.nWords())
})

// }}}
// {{{ lifecycle hooks

// In Manual Mode the user presses the phrase-split key after each phrase to mark a split point
// and advance to the next segment; pressing past the last segment stops the recording. The VAD's
// own silence events are ignored in Manual Mode (see onVoiceStop guard), so this is the only
// way segments advance while recording with automatic voice start/stop disabled.
function handleManualPhraseKey(e: KeyboardEvent): void {
  const action = manualPhraseAction({
    currentSegment: followerSegmentIndex.value,
    isRecording: isRecording.value,
    key: e.key,
    manualMode: disableAutomaticVoiceStartStop.value,
    nSegments: nFocusSegments.value,
    repeat: e.repeat,
  })
  if (!action.handled) {
    return
  }
  addManualSegmentSplit()
  highlightSegmentToSing(action.advanceTo)
  if (action.stop) {
    toggleRecord()
  }
}

onMounted((): void => {
  console.log('RecordingsView mounted')
  console.log('RecordingsView: state.sentences.length =', state.sentences.length)
  logger.debug('RecordingsView mounted')
  store.dispatch.audio.setLogger(logger)
  store.dispatch.user.fetchUser()
  store.dispatch.student.fetchTropes()

  watchSentences()
  console.log('RecordingsView: watchSentences() registered')
  window.addEventListener('keydown', handleManualPhraseKey)
})

onBeforeUnmount((): void => {
  window.removeEventListener('keydown', handleManualPhraseKey)
})

onBeforeRouteLeave((__, ___, next): void => {
  if (isRecording.value) {
    toggleRecord()
  }
  clearAllRefetchIntervals()
  next()
})

// }}}
// {{{ watchers
watch(phraseLevel, (newVal, oldVal) => {
  console.log('RecordingsView: phraseLevel changed', oldVal, '->', newVal, '(phrasing:', navigationComposable.phrasing.value, ')')
})

watch(
  () => state.sentences,
  (newVal) => {
    console.log('RecordingsView watch state.sentences fired, length =', newVal?.length)
    if (playing.value) {
      pause()
    }
  },
)

watch(
  () => store.getters.audio.recordings,
  (newVal) => {
    console.log('RecordingsView watch audio.recordings fired, keys =', Object.keys(newVal || {}))
  },
  { deep: true },
)

// When a new sentence is focused (not editing), compute segmentation at current phraseLevel.
// On first focus, default based on voice mode; subsequently the slider position persists
// and snaps to the nearest distinct level for the new sentence.
let phraseLevelInitialized = false
watch(focusSentence, (newSentence) => {
  if (playing.value) {
    pause()
  }
  if (!newSentence || existingSentenceRecordingMetadata.value) {
    return
  }
  nextTick(() => {
    if (!focusAnnotatedSentence.value) {
      return
    }
    if (!phraseLevelInitialized) {
      phraseLevel.value = inReadMode.value ? PHRASE_LEVEL_WORDS : PHRASE_LEVEL_SENTENCE
      phraseLevelInitialized = true
    } else if (availableLevels.value.length) {
      // Snap to nearest available level for this sentence
      phraseLevel.value = nearestAvailableLevel(availableLevels.value, phraseLevel.value)
    }
    segmentation.value = focusSegmentationForLevel(phraseLevel.value)
  })
})

// Auto-select pronunciation when there's only one choice (matching master)
watchEffect(() => {
  if (pronunciationChoices.value.length === 1) {
    nextTick(() => {
      recordingComposable.updateVoiceStyleSet({ pronunciation: pronunciationChoices.value[0] })
    })
  }
})

watch(focusAnnotatedSentence, () => setAutoTag())

watch(segmentation, () => setAutoTag(), { deep: true })

// }}}
// {{{ computed
const focusMode = computed(() => !!focusSentence.value)
const isColumnMode = computed(() => !!state.columnOverlay)

// }}}
// {{{ methods
// The editor changed how many words are trimmed at the column edges; mirror the
// persisted values onto the local overlay so the displayed column re-trims.
function onUpdateColumnTrim(skipPrefixWords: number, cutTrailingWords: number) {
  if (state.columnOverlay) {
    state.columnOverlay = { ...state.columnOverlay, cutTrailingWords, skipPrefixWords }
  }
}

// "+" past a zeroed edge pulls in the adjacent verse from outside the OCR'd range:
// the backend moves the range + reveals one new word; we reload the sentences via STSS.
async function onExtendColumnRange(edge: 'start' | 'end') {
  if (!state.columnOverlay) {
    return
  }
  const payload = await runReportedMutation(
    ExtendColumnRange,
    { edge, id: state.columnOverlay.id },
    'extendColumnRange',
    store.dispatch.snackbar.showError,
  )
  console.log('RecordingsView onExtendColumnRange:', edge, '→', payload)
  if (payload) {
    const updated = {
      ...state.columnOverlay,
      cutTrailingWords: payload.cutTrailingWords ?? state.columnOverlay.cutTrailingWords,
      firstSefariaId: payload.firstSefariaId ?? state.columnOverlay.firstSefariaId,
      lastSefariaId: payload.lastSefariaId ?? state.columnOverlay.lastSefariaId,
      skipPrefixWords: payload.skipPrefixWords ?? state.columnOverlay.skipPrefixWords,
    }
    state.columnOverlay = updated
    stssRef.value?.setColumnOverlay(updated)
  }
}

function handleCloseOverlay() {
  purgeAudioPlayer()
  closeRecordOverlay()
}

function handleRecordingEdit(
  sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios,
  annotatedSentence: AnnotatedSentence,
  sentenceIndex: number,
  cache = true,
  scroll = true,
) {
  purgeAudioPlayer()
  handleEditRecording(sentenceRecordingMetadata, annotatedSentence, sentenceIndex, cache, scroll)

  // For unsegmented recordings (e.g. full-sentence singing), segmentationFromRecording
  // returns []. Set a fallback so the text is clickable for play/re-record.
  if (!segmentation.value.length && focusAnnotatedSentence.value) {
    segmentation.value = isSung(sentenceRecordingMetadata)
      ? makeSentenceSegmentation(focusAnnotatedSentence.value)
      : makeWordSegmentation(focusAnnotatedSentence.value)
  }

  // Populate voiceStyleSet and phraseLevel from the recording being edited
  if (sentenceRecordingMetadata.sentenceRecording) {
    const sr = sentenceRecordingMetadata.sentenceRecording
    const populated = voiceStyleSetFromRecording(sr)
    recordingComposable.updateVoiceStyleSet(populated)
    autoSelectMelody()
    const tagPhrasing = phrasingFromTags(sr.tags)
    phraseLevel.value = tagPhrasing === WORDS ? PHRASE_LEVEL_WORDS : tagPhrasing === SENTENCE ? PHRASE_LEVEL_SENTENCE : DEFAULT_TROPE_LEVEL
    console.log(
      'handleRecordingEdit: recording tags (all):',
      sr.tags.map((t) => ({ hidden: t.hidden, name: t.name })),
    )
    console.log('handleRecordingEdit: populated voiceStyleSet', {
      phraseLevel: phraseLevel.value,
      pronunciation: populated.pronunciation?.description,
      segmentation: segmentation.value,
      tags: populated.tags?.map((t) => t.name),
      voice: populated.voice,
    })
  }
}

// Shared: update melodies on a recording when voice changes, then persist.
// Used by both the table voice dropdown and the focus-view voice style selector.
async function applyVoiceToRecording(metadata: SentenceRecordingMetadataWithPitchedAudios, isRead: boolean) {
  let melodies: RecordingMelodies = {}
  if (isRead) {
    melodies = { haftarahMelody: null, sentenceGroupMelody: null, torahMelody: null }
  } else if (state.aliyah) {
    melodies = { torahMelody: torahMelodyChoices.value[0] || null }
  } else if (state.haftarah) {
    melodies = { haftarahMelody: haftarahMelodyChoices.value[0] || null }
  } else if (state.sentenceGroup) {
    melodies = { sentenceGroupMelody: sentenceGroupMelodyChoices.value[0] || null }
  }
  if (await updateRecordingMelodies(metadata, melodies)) {
    store.dispatch.snackbar.add({ message: 'Recording updated.', state: 'primary' })
  }
}

// Quick-record: pre-configure voice style and open focus dialog
function handleQuickRecord(annotatedSentence: AnnotatedSentence, sentenceIndex: number, mode: 'reading' | 'singing') {
  console.log('handleQuickRecord:', { mode, sentenceIndex })

  // Open the focus dialog for this sentence (new recording, not editing)
  handleSentenceClick(annotatedSentence, sentenceIndex)

  // Pre-configure voice style based on mode
  const updates: Partial<VoiceStyleSet> = {
    voice: RecordingVoiceStyles[mode === VOICE_MODE_READING ? READ : SING],
  }

  // Auto-select pronunciation if only one choice
  if (pronunciationChoices.value.length === 1) {
    updates.pronunciation = pronunciationChoices.value[0]
  }

  recordingComposable.updateVoiceStyleSet(updates)

  // Auto-select melody for singing mode
  if (mode === VOICE_MODE_SINGING) {
    autoSelectMelody()
  }
}

watch(playing, (newVal, oldVal) => {
  console.log(`[RecordingsView] playing changed: ${oldVal} → ${newVal}`)
})

function handlePlaySegment(segmentIndex: number) {
  console.log('RecordingsView.handlePlaySegment', {
    hasMetadata: !!existingSentenceRecordingMetadata.value,
    playing: playing.value,
    segmentIndex,
  })
  if (!existingSentenceRecordingMetadata.value) {
    return
  }
  playSegment(existingSentenceRecordingMetadata.value, segmentIndex)
}

function handleUpdateVoiceStyleSet(value: VoiceStyleSet) {
  const oldVoice = voiceStyleSet.value.voice
  recordingComposable.updateVoiceStyleSet(value)
  autoSelectMelody()

  // When voice changes with an existing recording, update it on the backend
  const metadata = existingSentenceRecordingMetadata.value
  if (value.voice !== oldVoice && metadata?.sentenceRecording) {
    applyVoiceToRecording(metadata, inReadMode.value)
  }
}

function handleUpdatePhraseLevel(level: number) {
  phraseLevel.value = level
  if (!focusAnnotatedSentence.value) {
    console.log('handleUpdatePhraseLevel: no focusAnnotatedSentence, bailing')
    return
  }
  segmentation.value = focusSegmentationForLevel(level)
  console.log('handleUpdatePhraseLevel:', level, '-> segmentation =', JSON.stringify(segmentation.value))
  navigationComposable.customSegmentation.value = false
}

// Auto-add hidden tags (sentence/phrases/words) based on current segmentation
function setAutoTag(): void {
  if (!segmentation.value?.length || !focusAnnotatedSentence.value) {
    return
  }

  // Strip existing hidden tags
  const vss = voiceStyleSet.value
  const visibleTags = filterVisibleTags(vss.tags ?? [])

  const nWords = focusAnnotatedSentence.value.nWords()
  const hiddenTagsToAdd: string[] = []

  if (segmentation.value.length === nWords) {
    hiddenTagsToAdd.push(WORDS)
  }
  if (segmentation.value.length === 1) {
    hiddenTagsToAdd.push(SENTENCE)
  }
  if (!hiddenTagsToAdd.length || nWords <= MAX_WORDS_STILL_PHRASED) {
    hiddenTagsToAdd.push(PHRASES)
  }

  const userTags = store.getters.user.user?.tags ?? []
  const newTags = [...visibleTags]
  for (const name of hiddenTagsToAdd) {
    const tag = userTags.find((t: TagType) => t.name === name && t.hidden)
    if (tag) {
      newTags.push(tag)
    }
  }
  recordingComposable.updateVoiceStyleSet({ tags: newTags })
}

const canEditPhrases = () =>
  phraseLevel.value > PHRASE_LEVEL_SENTENCE &&
  phraseLevel.value < PHRASE_LEVEL_WORDS &&
  !existingSentenceRecordingMetadata.value &&
  segmentableSentence.value

// Merge two adjacent segments (click on segment boundary)
function handleSegmentBoundaryClick(
  _annotatedSentence: AnnotatedSentence,
  _annotatedSegment: unknown,
  _sentenceIndex: number,
  segmentIndex: number,
): void {
  if (!canEditPhrases()) {
    return
  }
  segmentation.value[segmentIndex] += segmentation.value[segmentIndex + 1]
  segmentation.value.splice(segmentIndex + 1, 1)
  navigationComposable.customSegmentation.value = true
}

// Split a segment at a word boundary
function handleWordBoundaryClick(
  _annotatedSentence: AnnotatedSentence,
  _annotatedSegment: unknown,
  _annotatedWord: unknown,
  _sentenceIndex: number,
  segmentIndex: number,
  wordIndex: number,
): void {
  if (!canEditPhrases()) {
    return
  }
  const originalWords = segmentation.value[segmentIndex]
  segmentation.value[segmentIndex] = originalWords - (wordIndex + 1)
  segmentation.value.splice(segmentIndex, 0, wordIndex + 1)
  navigationComposable.customSegmentation.value = true
}

function handleReRecordSegment(segmentIndex: number): void {
  console.log('handleReRecordSegment: segmentIndex =', segmentIndex, 'playing =', playing.value, 'isRecording =', isRecording.value)
  // Purge clears old audio so the next play loads fresh data after re-recording
  purgeAudioPlayer()
  state.replacementRecordingSegmentIndex = segmentIndex
  toggleRecord()
}

function handleStartRecord(mode: RecordingMode): void {
  console.log('handleStartRecord: mode =', mode, 'isRecording =', isRecording.value)
  state.recordingMode = mode
  state.manualStop = false
  state.replacementRecordingSegmentIndex = null
  toggleRecord()
}

function handleToggleRecord(): void {
  console.log('handleToggleRecord: isRecording =', isRecording.value)
  // If already recording, this is a manual stop — don't auto-advance in continuous mode
  if (isRecording.value) {
    state.manualStop = true
  }
  state.replacementRecordingSegmentIndex = null
  toggleRecord()
}

function uploadFile(): void {
  if (pendingUploadRecording.value) {
    upload(pendingUploadRecording.value)
  } else {
    logger.debug('unexpected null pendingUploadRecording in uploadFile()')
  }
}

// {{{ upload
function uploadRecording(recording: RecordingType): void {
  if (!focusSentence.value || focusSentenceIndex.value === null) {
    logger.debug('null focusSentence or focusSentenceIndex in uploadRecording')
    return
  }

  const blob = new Blob([recording.blob], { type: recording.mimeType })
  const extension = mimeToExtension(blob.type)
  const parashah = state.haftarah ? state.haftarah.parashah : state.parashah
  const vss = voiceStyleSet.value

  let fileName = ''
  if (state.aliyah) {
    fileName += `Par-${parashah!.transliteratedTitle}-Ali-${state.aliyah.rank}`
  } else if (state.haftarah) {
    fileName += `Haf-${parashah!.transliteratedTitle}`
  } else if (state.sentenceGroup) {
    fileName += `Txt-${state.sentenceGroup.reading.description}`
  }
  fileName += `-Sen-${focusSentenceIndex.value + 1}`
  fileName += `-Pron-${vss.pronunciation?.description}`
  if (vss.torahMelody) {
    fileName += `-TMel-${vss.torahMelody.description}`
  } else if (vss.haftarahMelody) {
    fileName += `-HM${vss.haftarahMelody.description}`
  } else if (vss.sentenceGroupMelody) {
    fileName += `-SGM${vss.sentenceGroupMelody.description}`
  }
  fileName += `-${moment().format('YYYY-MM-DD-hh-mm-ss')}`
  fileName += `.${extension}`

  const file = new File([blob], fileName, { type: recording.mimeType })
  upload(file)
}

function upload(file: File | Blob): void {
  if (!focusAnnotatedSentence.value) {
    logger.debug('upload: unexpected null focusAnnotatedSentence')
    return
  }
  if (focusSentenceIndex.value === null) {
    logger.debug('upload: unexpected null focusSentenceIndex')
    return
  }

  const sr = existingSentenceRecordingMetadata.value?.sentenceRecording ?? null
  const vss = voiceStyleSet.value
  const isSing = inSingMode(vss)

  uploadWaiting.value = true
  runReportedMutation(
    UploadRecording,
    {
      aliyah: state.aliyah?.id ?? null,
      autoSegment: recordingComposable.autoSegmentDetect.value,
      file,
      haftarah: state.haftarah?.reading?.id ?? null,
      haftarahMelodyStyle: isSing && vss.haftarahMelody ? vss.haftarahMelody.id : null,
      manualSegments: recordingComposable.manualSegmentSplitOffsets.value,
      nwords: focusAnnotatedSentence.value.nWords(),
      optionalFilename: recordingComposable.optionalFilename.value,
      pronunciation: vss.pronunciation?.id,
      replacesSegment: state.replacementRecordingSegmentIndex ?? -1,
      segmentedSentence: sr?.segmentedSentence?.id ?? null,
      segments: (() => {
        const isStandard = navigationComposable.hasStandardSegmentation(inReadMode.value)
        const seg = isStandard ? [] : segmentation.value
        console.log('UPLOAD segments:', {
          followerSegmentIndex: followerSegmentIndex.value,
          inReadMode: inReadMode.value,
          isStandard,
          nFocusSegments: nFocusSegments.value,
          nwords: focusAnnotatedSentence.value!.nWords(),
          segmentation: segmentation.value,
          sentSegments: seg,
        })
        return seg
      })(),
      sentence: focusSentence.value?.id ?? null,
      sentenceGroup: state.sentenceGroup?.reading?.id ?? null,
      sentenceGroupMelodyStyle: isSing && vss.sentenceGroupMelody ? vss.sentenceGroupMelody.id : null,
      sentenceRecording: sr?.id ?? null,
      tags: vss.tags ? vss.tags.map((tag: TagType) => tag.id) : [],
      torahMelodyStyle: isSing && vss.torahMelody ? vss.torahMelody.id : null,
    },
    'uploadRecording',
    (message) => store.dispatch.dialog.show({ message, title: UPLOAD_FAILED_TITLE }),
  )
    .then((uploadData) => {
      if (!uploadData) {
        return
      }

      if (sr) {
        store.dispatch.audio.releaseRecording({ sentenceRecording: sr })
      }
      console.log('upload: releaseRecording done, fetching new recording id =', uploadData.id)

      // Return the promise so .finally() waits for fetchRecording to complete
      return store.dispatch.audio.fetchRecording({ id: parseInt(uploadData.id!) }).then(() => {
        console.log('upload: fetchRecording done, replacementRecordingSegmentIndex =', state.replacementRecordingSegmentIndex)

        // For segment replacement, re-focus on the updated recording
        if (state.replacementRecordingSegmentIndex !== null) {
          const sentenceId = focusSentence.value?.id
          const newRecordingId = String(uploadData.id)
          const freshMetadata = sentenceId ? store.getters.audio.getRecordingsForSentence(sentenceId)[newRecordingId] ?? null : null
          const savedAnnotated = focusAnnotatedSentence.value
          const savedIndex = focusSentenceIndex.value
          console.log(
            'upload: segment replacement, freshMetadata =',
            !!freshMetadata,
            'sentenceId =',
            sentenceId,
            'newRecordingId =',
            newRecordingId,
          )
          closeRecordOverlay()
          if (freshMetadata && savedAnnotated && savedIndex !== null) {
            handleRecordingEdit(freshMetadata, savedAnnotated, savedIndex, false, false)
          }
          return
        }

        // For full recordings, auto-advance and auto-start in continuous mode, or close.
        // If the user manually pressed Stop, always close (don't auto-advance).
        const focusIndex = focusSentenceIndex.value
        if (
          focusIndex !== null &&
          shouldAutoAdvanceAfterUpload({
            canProceedToNext: canProceedToSentence(focusIndex + 1),
            manualStop: state.manualStop,
            recordingMode: state.recordingMode,
          })
        ) {
          proceedToSentence(focusIndex + 1)
          nextTick(() => toggleRecord())
        } else {
          closeRecordOverlay()
        }
      })
    })
    .catch((error) => {
      console.error('upload: refreshing the recording failed:', error)
      store.dispatch.dialog.show({ message: MUTATION_FAILED_MESSAGE, title: UPLOAD_FAILED_TITLE })
    })
    .finally(() => {
      uploadWaiting.value = false
      state.replacementRecordingSegmentIndex = null
      state.manualStop = false
      // Clear the chosen file so its name doesn't linger in the upload input for the next upload.
      pendingUploadRecording.value = null
    })
}
// }}}

// }}}

// }}}
</script>

<template>
  <v-container fluid>
    <!-- Focus Mode - SentenceFocus in a modal dialog -->
    <v-dialog
      :model-value="focusMode"
      :opacity="FOCUS_DIALOG_SCRIM_OPACITY"
      scrim="black"
      :width="FOCUS_DIALOG_WIDTH"
      @keydown.esc="handleCloseOverlay"
      @update:model-value="!$event && handleCloseOverlay()"
    >
      <sentence-focus
        v-if="focusMode"
        :aliyah="state.aliyah"
        :annotated-segments="[]"
        :available-levels="availableLevels"
        :background-sampling="backgroundSampling"
        :background-sampling-percent="backgroundSamplingPercent"
        :bar-mitzvah="state.barMitzvah"
        :can-proceed-to-next="canProceedToNext"
        :can-proceed-to-previous="canProceedToPrevious"
        :chart-data="chartData"
        :chart-options="chartOptions"
        :current-segment-index="followerSegmentIndex"
        :disable-automatic-voice-start-stop="disableAutomaticVoiceStartStop"
        :display-graph="displayGraph"
        :existing-sentence-recording-metadata-with-pitched-audios="existingSentenceRecordingMetadata"
        :focus-sentence="focusSentence"
        :focus-sentence-index="focusSentenceIndex"
        :follower-segment-style="followerSegmentStyle"
        :haftarah="state.haftarah"
        :haftarah-melody-choices="haftarahMelodyChoices"
        :in-read-mode="inReadMode"
        :is-melodic-style-disabled="isMelodicStyleDisabled"
        :is-ready-for-recording="isReadyForRecording"
        :is-recording="isRecording"
        :non-hidden-user-tags="nonHiddenUserTags"
        :paused="paused"
        :pending-upload-recording="pendingUploadRecording"
        :phrase-level="phraseLevel"
        :phrasing-dropdown-disabled="phrasingDropdownDisabled"
        :playing="playing"
        :pronunciation-choices="pronunciationChoices"
        :segmentable-sentence="segmentableSentence"
        :segmentation-length="segmentation.length"
        :segmentations="segmentations"
        :sentence-group="state.sentenceGroup"
        :sentence-group-melody-choices="sentenceGroupMelodyChoices"
        :show-start-now="showStartNow"
        :torah-melody-choices="torahMelodyChoices"
        :transformation="transformation"
        :upload-waiting="uploadWaiting"
        :user-is-logged-in="userIsLoggedIn"
        :voice-style-set="voiceStyleSet"
        @click-segment-boundary="handleSegmentBoundaryClick"
        @click-word-boundary="handleWordBoundaryClick"
        @close="handleCloseOverlay"
        @pause-segment="pause"
        @play-segment="handlePlaySegment"
        @proceed-to-next="proceedToSentence((focusSentenceIndex ?? 0) + 1)"
        @proceed-to-previous="proceedToSentence((focusSentenceIndex ?? 0) - 1)"
        @re-record-segment="handleReRecordSegment"
        @resume-segment="resume"
        @start-record="handleStartRecord"
        @stop-segment="pause"
        @toggle-record="handleToggleRecord"
        @update-disable-automatic-voice-start-stop="(v: boolean) => (recordingComposable.disableAutomaticVoiceStartStop.value = v)"
        @update-display-graph="(v: boolean) => (recordingComposable.displayGraph.value = v)"
        @update-pending-upload-recording="(v: File | null) => (recordingComposable.pendingUploadRecording.value = v)"
        @update-phrase-level="handleUpdatePhraseLevel"
        @update-voice-style-set="handleUpdateVoiceStyleSet"
        @upload-file="uploadFile"
        @warn-manual-segmentation="warnManualSegmentation"
      />
    </v-dialog>

    <!-- Overview Mode - Show sentence selection and overview -->
    <div>
      <!-- Text Selection -->
      <student-to-sentences-select
        ref="stssRef"
        v-model:aliyah="state.aliyah"
        v-model:bar-mitzvah="state.barMitzvah"
        v-model:column-overlay="state.columnOverlay"
        v-model:haftarah="state.haftarah"
        v-model:parashah="state.parashah"
        v-model:sentence-group="state.sentenceGroup"
        v-model:sentences="state.sentences"
        :aliyah-disabled="false"
        :aliyah-reset-on-options-change="false"
        :bar-mitzvah-clearable="true"
        :bar-mitzvah-searchable="true"
        :disable-haftarah-when-student-has-no-haftarah="true"
        :disable-parashah-when-student-has-no-parashah="true"
        :disable-sentence-group-when-student-has-no-sentence-group="true"
        :force-student-select="false"
        :haftarah-disabled="false"
        :haftarah-disabled-on-no-bar-mitzvah="false"
        :haftarah-reset-on-options-change="true"
        :hide-aliyah-when-disabled="true"
        :hide-haftarah-when-disabled="true"
        :hide-parashah-when-disabled="true"
        :hide-sentence-group-when-disabled="true"
        :hide-student-when-disabled="true"
        :no-sentence-select="true"
        :no-student-select="false"
        :parashah-disabled="false"
        :parashah-disabled-on-no-bar-mitzvah="false"
        :parashah-reset-on-options-change="true"
        :prevent-auto-single-aliyah-select="false"
        :prevent-auto-single-bar-mitzvah-select="true"
        :prevent-auto-single-haftarah-select="true"
        :prevent-auto-single-parashah-select="true"
        :prevent-auto-single-sentence-group-select="true"
        :sentence-group-disabled-on-no-bar-mitzvah="false"
        :student-disabled-when-other-selected="true"
        student-dropdown-text="Choosing a student will filter choices down to those assigned to the student; clearing the student selection makes all text available for recording."
        @column-analyzed="state.columnAutoEdit = true"
      />

      <!-- Full-Text Overview -->
      <full-text-overview
        v-if="state.sentences.length > 0"
        :annotated-sentences="annotatedSentences"
        :auto-edit="state.columnAutoEdit"
        :bar-mitzvah="state.barMitzvah"
        :column-overlay="state.columnOverlay"
        :downloading-sentence-recording-id="downloadingSentenceRecordingId"
        :is-aliyah="!!state.aliyah"
        :is-column-mode="isColumnMode"
        :paused="paused"
        :playing="playing"
        :playing-sentence-recording-id="playingSentenceRecordingId"
        :print-title="printTitle"
        :sentence-recording-metadatas="sentenceRecordingMetadatas"
        :sentences="state.sentences"
        :user-is-logged-in="userIsLoggedIn"
        @auto-edit-consumed="state.columnAutoEdit = false"
        @delete-recording="confirmDeleteRecording"
        @download-recording="fetchRecordingForDownload"
        @edit-recording="handleRecordingEdit"
        @extend-column-range="onExtendColumnRange"
        @handle-sentence-click="handleSentenceClick"
        @pause-recording="pause"
        @play-recording="playSentenceOrSequencedSegments"
        @quick-record="handleQuickRecord"
        @resume-recording="resume"
        @stop-recording="purgeAudioPlayer"
        @update-column-trim="onUpdateColumnTrim"
      />
    </div>
  </v-container>
</template>

<style scoped>
a:hover {
  text-decoration: none;
}

a.disabled {
  pointer-events: none;
  color: grey;
}
</style>
